export type PresenterKind = 'rca' | 'pm' | 'summary' | 'generic'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function hasString(result: Record<string, unknown>, key: string): boolean {
  return typeof result[key] === 'string' && result[key].trim().length > 0
}

function hasArray(result: Record<string, unknown>, key: string): boolean {
  return Array.isArray(result[key])
}

/**
 * RCA emit documents are identifiable from keys alone. Extra keys still
 * match; a partial document (headline without the arrays) does not.
 */
export function matchesRca(result: unknown): boolean {
  if (!isRecord(result)) return false
  return (
    hasString(result, 'headline') &&
    hasArray(result, 'root_causes') &&
    hasArray(result, 'impacts') &&
    hasArray(result, 'action_items')
  )
}

/** PM emit documents: predictions + action_items. RCA is checked first. */
export function matchesPm(result: unknown): boolean {
  if (!isRecord(result)) return false
  return hasArray(result, 'predictions') && hasArray(result, 'action_items')
}

/**
 * Period-summary emit documents. RCA/PM are checked first so a headline
 * plus leftover arrays cannot steal this family.
 */
export function matchesSummary(result: unknown): boolean {
  if (!isRecord(result)) return false
  return (
    hasString(result, 'headline') && hasString(result, 'narrative') && hasArray(result, 'metrics')
  )
}

/**
 * First matching presenter wins. Schema is accepted as a hint but never
 * required — real runs may omit `config_snapshot.output_schema`.
 */
export function pickPresenter(result: unknown, _schema?: unknown): PresenterKind {
  if (matchesRca(result)) return 'rca'
  if (matchesPm(result)) return 'pm'
  if (matchesSummary(result)) return 'summary'
  return 'generic'
}

export function presenterHeadline(kind: PresenterKind, result: unknown): string | undefined {
  if ((kind !== 'rca' && kind !== 'summary') || !isRecord(result)) return undefined
  const headline = result.headline
  return typeof headline === 'string' && headline.trim() ? headline.trim() : undefined
}
